import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { signup } from "../redux/actions/auth";
import ReCAPTCHA from "react-google-recaptcha";

const schema = yup.object({
  company_name: yup
    .string()
    .required("Company name is required.")
    .matches(
      /^[^\s]+(?:$|.*[^\s]+$)/,
      "Blank space not allowed in company name."
    )
    .max(100, "Company name must be equal or less than 100 characters."),
  email: yup
    .string()
    .email("Please enter valid email address.")
    .required("Email is required.")
    .max(100, "Email must be equal or less than 100 characters."),
  password: yup
    .string()
    .required("Password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/,
      "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/,
      "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"
    )
    .oneOf(
      [yup.ref("password"), null],
      "Password and Confirm Password must match"
    ),
});

const disabledCss =
  "disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed";

const Signup = ({ user, setUser }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [reCaptchaToken, setReCaptchaToken] = useState();
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
  const [termsAndCondition, setTermsAndCondition] = useState(false);
  const [termAndConditionError, setTermAndConditionError] = useState(false);

  // useEffect to load the google reCaptcha script once component is fully rendered
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const captchaRef = useRef(null);

  // Function to store the captcha token
  const handleCaptchaClick = (token) => {
    setReCaptchaToken(token);
    setIsCaptchaChecked(!isCaptchaChecked);
  };

  const defaultValues = {
    company_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const onSubmit = async (data) => {
    setLoading(true);
    data.captchaToken = reCaptchaToken;

    const payload = {
      ...data,
      planId: searchParams.get("planid") ? searchParams.get("planid") : null,
    };

    if (!termsAndCondition) {
      setTermAndConditionError(true);
      setLoading(false);
      return;
    }

    dispatch(
      signup(payload, (res) => {
        setLoading(false);
        captchaRef.current.reset();
        if (res.status === 1) {
          setError({
            type: "success",
            message: "User successfully registered.",
          });
        } else {
          setError({
            type: "error",
            message: res.message,
          });
        }
      })
    );

    reset(defaultValues);
    setTermsAndCondition(false);
  };

  return (
    <main className="bg-white min-h-screen relative md:flex overflow-y-auto">
      {/* Image */}
      <div className="hidden md:block md:w-1/2" aria-hidden="true">
        <img
          className="object-cover object-center w-full h-full"
          src={require("../assets/img/login-image.jpeg")}
          // width="760"
          // height="1024"
          alt="Authentication"
        />
      </div>
      {/* Content */}
      <div className="h-full md:w-1/2 after:flex-1 overflow-y-auto">
        {/* max-w-sm mx-auto h-[100vh] flex items-center */}
        {/* <div className="max-w-sm mx-auto h-full"> */}
        <div className="max-w-sm mx-auto h-[100vh] flex items-center overflow-y-auto">
          {/* <div className="px-2 py-4 lg:px-4 lg:py-8 h-full"> */}
          <div className="px-4 py-8 w-full">
            {/* Header */}
            <div className="flex flex-1 mb-3 lg:mb-5">
              <div className="flex items-center">
                {/* Logo */}
                <div className="block w-[40px]">
                  <img
                    alt="Logo"
                    src={require("../assets/img/ShiproneLogo.png")}
                  />
                </div>
              </div>
            </div>

            <h1 className="text-3xl text-slate-800 font-bold mb-3 lg:mb-5">
              Create Your Account
            </h1>

            {/* Error Message */}

            <div className="my-1 lg:my-3">
              {!error ? (
                ""
              ) : (
                <div
                  className={` ${
                    error.type === "error"
                      ? "bg-amber-100 text-amber-600"
                      : "bg-green-600 text-white"
                  } px-3 py-2 rounded`}
                >
                  <svg
                    className="inline w-3 h-3 shrink-0 fill-current mr-2"
                    viewBox="0 0 12 12"
                  >
                    <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                  </svg>
                  <span className="text-sm">{error.message}</span>
                </div>
              )}
            </div>

            {/* Form */}
            <form className="space-y-2 lg:space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2 lg:space-y-4">
                <Controller
                  name="company_name"
                  defaultValue={defaultValues.company_name}
                  control={control}
                  render={({ field, formState }) => (
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="company_name"
                      >
                        Company Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        {...field}
                        // name="company"
                        // id="company"
                        className="form-input w-full"
                        type="text"
                      />
                      {formState.errors?.company_name && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formState.errors?.company_name?.message}
                        </div>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="email"
                  defaultValue={defaultValues.email}
                  control={control}
                  render={({ field, formState }) => (
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="email"
                      >
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        {...field}
                        // name="email"
                        // id="email"
                        className="form-input w-full"
                        type="email"
                        autoComplete="off"
                      />
                      {formState.errors?.email && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formState.errors?.email?.message}
                        </div>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="password"
                  defaultValue={defaultValues.password}
                  control={control}
                  render={({ field, formState }) => (
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="password"
                      >
                        Password <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          {...field}
                          className="form-input w-full"
                          type={showPassword ? "text" : "password"}
                          autoComplete="off"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                          {showPassword && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="w-4 h-4 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPassword(!showPassword);
                              }}
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          )}
                          {!showPassword && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="w-4 h-4 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPassword(!showPassword);
                              }}
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      {formState.errors?.password && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formState.errors?.password?.message}
                        </div>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="confirmPassword"
                  defaultValue={defaultValues.confirmPassword}
                  control={control}
                  render={({ field, formState }) => (
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="confirmPassword"
                      >
                        Confirm Password{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          {...field}
                          className="form-input w-full"
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="off"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                          {showConfirmPassword && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="w-4 h-4 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowConfirmPassword(!showConfirmPassword);
                              }}
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          )}
                          {!showConfirmPassword && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="w-4 h-4 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowConfirmPassword(!showConfirmPassword);
                              }}
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      {formState.errors?.confirmPassword && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formState.errors?.confirmPassword?.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="term&condition"
                  type="checkbox"
                  name="term&condition"
                  className={`form-checkbox ${
                    termAndConditionError && "!border-red-500"
                  }`}
                  checked={termsAndCondition}
                  onChange={(e) => {
                    setTermsAndCondition(e.target.checked);
                    setTermAndConditionError(false);
                  }}
                />

                <label htmlFor="term&condition" className="text-xs">
                  <span
                    className={`text-xs ${
                      termAndConditionError ? "text-red-500" : ""
                    } cursor-pointer`}
                  >
                    I have read & agree to the&nbsp;
                  </span>
                  <a
                    href="https://www.shiperone.com/term_and_condition"
                    target="__blank"
                    className={`text-xs font-semibold ${
                      termAndConditionError
                        ? "text-red-500"
                        : "text-indigo-500"
                    } cursor-pointer`}
                  >
                    terms & conditions
                  </a>
                </label>
              </div>

              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaClick}
                ref={captchaRef}
              />

              {isCaptchaChecked && (
                <div className="flex items-center justify-between mt-3 lg:mt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className={
                      "flex items-center flex-initial btn !bg-indigo-500 hover:!bg-indigo-600 text-white whitespace-nowrap w-full " +
                      (!loading ? "" : disabledCss)
                    }
                  >
                    Sign Up
                    {loading && (
                      <span className="ml-2">
                        <svg
                          className="animate-spin w-4 h-4 fill-current shrink-0"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                        </svg>
                      </span>
                    )}
                  </button>
                </div>
              )}
            </form>
            {/* Footer */}
            <div className="pt-5 mt-6 border-t border-slate-200">
              <div className="text-sm">
                Have an account?{" "}
                <Link
                  className="font-medium text-indigo-500 hover:text-indigo-600"
                  to="/login"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Signup;
